import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { useQueries } from '@tanstack/react-query';
import classNames from 'classnames';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import { BookingEntity } from 'Models/Entities';
import { transactionPricingQueryOptions } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import { BookingPricingResult } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { Journey } from 'Global/Journey/Types/Journey';
import { Expander } from 'Views/Components/Expander/Expander';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { Link } from 'react-router-dom';
import LoadingWave from 'Views/Components/_HumanWritten/Lottie/LoadingWave';
import StyledQrCode from 'Views/Components/_HumanWritten/StyleQrCode';
import { checkInUrl } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/BookingDetailsQrCode';
import { BookingWizardCartFields } from '../../../BookingWizardTypes';
import { calculateBookingsToDisplay } from '../TripBookingSummaryHelpers';
import { BookingName, TripType } from './ClassBookingSummaryCard';
import ClassBookingSummaryTotal from './ClassBookingSummaryTotal';
import { LegDate, LegDirection, LegGroups, LegRoute, LegSummary, LegTotal, useClassLeg } from './ClassLegParts';

export interface ClassBookingSuccessSummaryProps {
	/** One group per transaction: pay later splits a cart into a transaction per booking. */
	bookingGroups: { transactionId: string; bookings: BookingEntity[] }[];
	/** Guests have no account to manage a booking from. */
	hideLinks?: boolean;
}

interface SuccessLeg {
	key: string;
	departing: boolean;
	journey: Journey;
	bookingId: string;
	reference: string;
	cartFields: BookingWizardCartFields;
	bookingInfo: BookingPricingResult;
	isAlteration: boolean;
}

interface SuccessBooking {
	key: string;
	cartFields: BookingWizardCartFields;
	isAlteration: boolean;
	legs: SuccessLeg[];
}

/**
 * `Manage / View`, a small grey outline button at the end of the route line (design 11c's placement). Not
 * offered to guests, who have no account to manage a booking from.
 */
function ManageBooking({ leg, hideLinks }: { leg: SuccessLeg; hideLinks?: boolean }) {
	if (hideLinks) {
		return null;
	}

	return (
		<Link
			to={`/bookings/${leg.bookingId}`}
			className="class-booking-success__manage"
			data-test-id="class-booking-success-manage"
		>
			<FormattedMessage id="class-booking-summary__manage-view" defaultMessage="Manage / View" />
		</Link>
	);
}

/** `#1YW3Q4QHYY`, opening the booking — the same place `Manage / View` goes. Plain text for a guest. */
function BookingReference({ leg, hideLinks }: { leg: SuccessLeg; hideLinks?: boolean }) {
	if (hideLinks) {
		return <span className="class-booking-success__reference">#{leg.reference}</span>;
	}

	return (
		<Link
			to={`/bookings/${leg.bookingId}`}
			className="class-booking-success__reference class-booking-success__reference--link"
			data-test-id="class-booking-success-reference"
		>
			#{leg.reference}
		</Link>
	);
}

/** The route, with `Manage / View` at the far end of the same line. */
function RouteLine({ leg, hideLinks, className }: { leg: SuccessLeg; hideLinks?: boolean; className: string }) {
	return (
		<div className="class-booking-success__route-line">
			<LegRoute journey={leg.journey} className={className} />
			<ManageBooking leg={leg} hideLinks={hideLinks} />
		</div>
	);
}

/**
 * `Show QR code`, closed by default, with the code in the app's QR frame beneath it. Used on both web
 * and phone: the code is shown at check-in from a phone or the confirmation email, so it does not need
 * to take up the page until it is asked for.
 */
function QrCodeExpander({ qrCodeId }: { qrCodeId?: string }) {
	if (!qrCodeId) {
		return null;
	}

	return (
		<Expander>
			<Expander.ClickArea className="class-booking-success__expander">
				<span className="class-booking-success__expander-label">
					<Icon name="qr_code_2" useMaterialIcons classname="class-booking-success__qr-icon" />
					<FormattedMessage id="class-booking-summary__show-qr-code" defaultMessage="Show QR code" />
				</span>
				<Expander.Icon />
			</Expander.ClickArea>
			<Expander.Body>
				<div className="class-booking-success__qr-panel">
					<StyledQrCode value={checkInUrl(qrCodeId)} height="200px" width="200px" />
					<span className="class-booking-summary__muted">
						<FormattedMessage
							id="class-booking-summary__show-at-check-in"
							defaultMessage="Show this at check-in"
						/>
					</span>
				</div>
			</Expander.Body>
		</Expander>
	);
}

/** 5a's right-hand panel: the selected leg — its heading, its QR code behind an expander, its lines. */
function LegDetail({ leg, hideLinks }: { leg: SuccessLeg; hideLinks?: boolean }) {
	const lines = useClassLeg(leg.bookingInfo, leg.isAlteration);

	return (
		<div className="class-booking-success__detail" data-test-id="class-booking-success-detail">
			<div className="class-booking-success__detail-header">
				<div>
					<div className="class-booking-success__detail-eyebrow">
						<span>
							<BookingName
								tabSelected={leg.cartFields.wizardData.tabSelected}
								isAlteration={leg.isAlteration}
							/>
							{' · '}
							<LegDirection departing={leg.departing} />
						</span>
						<BookingReference leg={leg} hideLinks={hideLinks} />
					</div>
					<RouteLine leg={leg} hideLinks={hideLinks} className="class-booking-success__detail-route" />
					<LegDate journey={leg.journey} />
				</div>
			</div>
			<QrCodeExpander qrCodeId={leg.bookingInfo.qrCodeId} />
			{lines && (
				<div className="class-booking-success__detail-lines">
					<LegGroups leg={lines} isAlteration={leg.isAlteration} />
					<LegTotal
						leg={lines}
						departing={leg.departing}
						isAlteration={leg.isAlteration}
						className="class-booking-success__detail-total"
					/>
				</div>
			)}
		</div>
	);
}

/** 8b: one card per leg, its QR code and its lines each behind an expander. */
function MobileLegCard({ leg, hideLinks }: { leg: SuccessLeg; hideLinks?: boolean }) {
	const lines = useClassLeg(leg.bookingInfo, leg.isAlteration);

	return (
		<div className="class-booking-success__leg-card" data-test-id="class-booking-success-leg">
			<div className="class-booking-success__leg-card-heading">
				<div className="class-booking-success__leg-card-top">
					<span className="class-booking-summary__eyebrow">
						<LegDirection departing={leg.departing} />
					</span>
					<BookingReference leg={leg} hideLinks={hideLinks} />
				</div>
				<RouteLine leg={leg} hideLinks={hideLinks} className="class-booking-success__leg-card-route" />
				<LegDate journey={leg.journey} />
				{lines && <LegSummary leg={lines} />}
			</div>
			<QrCodeExpander qrCodeId={leg.bookingInfo.qrCodeId} />
			{lines && (
				<Expander>
					<Expander.ClickArea className="class-booking-success__expander">
						<span className="class-booking-success__expander-label">
							<FormattedMessage id="class-booking-summary__more-details" defaultMessage="More details" />
							<Expander.Icon />
						</span>
						<span className="class-booking-success__expander-price">
							{formatPriceDisplay(lines.total, leg.isAlteration)}
						</span>
					</Expander.ClickArea>
					<Expander.Body>
						<div className="class-booking-success__more-details">
							<LegGroups leg={lines} isAlteration={leg.isAlteration} />
							<LegTotal
								leg={lines}
								departing={leg.departing}
								isAlteration={leg.isAlteration}
								className="class-booking-success__more-details-total"
							/>
						</div>
					</Expander.Body>
				</Expander>
			)}
		</div>
	);
}

/**
 * The booking success page's summary on a site with fare types: 5a on web (totals and the list of
 * legs on the left, the chosen leg on the right), 8b on a phone (totals first, then a card per leg).
 */
function ClassBookingSuccessSummary({ bookingGroups, hideLinks }: ClassBookingSuccessSummaryProps) {
	const { isMobileBookingWizard } = useDeviceDetect();

	// Shared with BookingSuccessOuterWrapper's grand total, which starts these on mount.
	const pricing = useQueries({
		queries: bookingGroups.map(group => transactionPricingQueryOptions(group.transactionId, true, false)),
	});

	const loaded = pricing.every(x => x.isSuccess);

	const bookings = useMemo<SuccessBooking[]>(() => {
		if (!loaded) {
			return [];
		}

		return bookingGroups.flatMap((group, groupIndex) => {
			const transaction = pricing[groupIndex].data;
			const isAlteration = transaction?.isAlteration ?? false;
			const cartFieldsList = calculateBookingsToDisplay(
				group.bookings,
				false,
				true,
				isAlteration,
				'booking-success',
			);

			return (cartFieldsList ?? []).flatMap((cartFields, bookingIndex) => {
				if (!cartFields) {
					return [];
				}
				const booking =
					group.bookings.find(x => x.id === cartFields.wizardData.bookingToEdit) ?? group.bookings[0];
				const linked = transaction?.linkedBookings.find(
					x => x.departureBooking.bookingId === cartFields.wizardData.bookingToEdit,
				);
				const key = `${groupIndex}-${bookingIndex}`;
				const legs: SuccessLeg[] = [];

				if (cartFields.departingJourney && linked?.departureBooking) {
					legs.push({
						key: `${key}-departing`,
						departing: true,
						journey: cartFields.departingJourney,
						bookingId: booking.id,
						reference: booking.humanReadableId,
						cartFields,
						bookingInfo: linked.departureBooking,
						isAlteration,
					});
				}
				if (cartFields.returningJourney && linked?.returnBooking && booking.returnBooking) {
					legs.push({
						key: `${key}-returning`,
						departing: false,
						journey: cartFields.returningJourney,
						bookingId: booking.returnBooking.id,
						reference: booking.returnBooking.humanReadableId,
						cartFields,
						bookingInfo: linked.returnBooking,
						isAlteration,
					});
				}

				return [{ key, cartFields, isAlteration, legs }];
			});
		});
		// `pricing` is a new array every render; `loaded` and the data it carries are what move.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bookingGroups, loaded, ...pricing.map(x => x.data)]);

	const allLegs = bookings.flatMap(x => x.legs);
	const [selectedKey, setSelectedKey] = useState<string | null>(null);
	const selected = allLegs.find(x => x.key === selectedKey) ?? allLegs[0];

	if (!loaded) {
		return <LoadingWave>Loading summary</LoadingWave>;
	}

	const single = bookingGroups.length === 1;
	const combinedTotal = single
		? undefined
		: {
				total: pricing.reduce((sum, x) => sum + (x.data?.total ?? 0), 0),
				gst: pricing.reduce((sum, x) => sum + (x.data?.lines?.find(line => line.key === 'Gst')?.price ?? 0), 0),
			};

	const totals = (
		<ClassBookingSummaryTotal
			transactionPricingResults={pricing[0].data ?? undefined}
			location="booking-success"
			combinedTotal={combinedTotal}
		/>
	);

	const heading = (
		<h4 className="class-booking-success__heading">
			<FormattedMessage id="class-booking-summary__heading" defaultMessage="Booking summary" />
		</h4>
	);

	if (isMobileBookingWizard) {
		return (
			<div className="class-booking-success class-booking-success--mobile" data-test-id="class-booking-success">
				{heading}
				<div className="class-booking-success__stack">
					{totals}
					{allLegs.map(leg => (
						<MobileLegCard key={leg.key} leg={leg} hideLinks={hideLinks} />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="class-booking-success" data-test-id="class-booking-success">
			{heading}
			<div className="class-booking-success__layout">
				<div className="class-booking-success__master">
					{totals}
					{bookings.map(booking => (
						<div className="class-booking-success__booking" key={booking.key}>
							<div className="class-booking-success__booking-header">
								<span className="class-booking-success__booking-name">
									<BookingName
										tabSelected={booking.cartFields.wizardData.tabSelected}
										isAlteration={booking.isAlteration}
									/>
								</span>
								<span>
									<TripType oneWay={booking.legs.length < 2} />
								</span>
							</div>
							{booking.legs.map(leg => {
								const active = leg.key === selected?.key;
								return (
									<button
										type="button"
										key={leg.key}
										className={classNames('class-booking-success__leg', {
											'class-booking-success__leg--active': active,
										})}
										aria-pressed={active}
										onClick={() => setSelectedKey(leg.key)}
										data-test-id="class-booking-success-leg"
									>
										<div>
											<LegRoute
												journey={leg.journey}
												className="class-booking-success__leg-route"
											/>
											<div className="class-booking-success__leg-sub">
												<LegDate journey={leg.journey} plain /> · #{leg.reference}
											</div>
										</div>
										<span className="class-booking-success__leg-chevron" aria-hidden>
											›
										</span>
									</button>
								);
							})}
						</div>
					))}
				</div>
				{/* Keyed so the QR code closes again when another leg is chosen. */}
				{selected && <LegDetail key={selected.key} leg={selected} hideLinks={hideLinks} />}
			</div>
		</div>
	);
}

export default observer(ClassBookingSuccessSummary);
